import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening â€” updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first â€” otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

class InvoiceFieldsComponent extends HTMLElement {
  #saveTimer;
  #visibilityTimer;
  #dirty = false;
  #saving = false;

  connectedCallback() {
    this.toggle = this.querySelector('[data-invoice-toggle]');
    this.panel = this.querySelector('[data-invoice-panel]');
    this.fields = Array.from(this.querySelectorAll('[data-invoice-field]'));
    this.status = this.querySelector('[data-invoice-status]');
    this.summary = this.closest('.cart-drawer__summary');

    if (!this.toggle || !this.panel) return;

    this.toggle.addEventListener('change', this.#handleToggle);
    this.fields.forEach((field) => {
      field.addEventListener('input', this.#handleInput);
      field.addEventListener('change', this.#handleInput);
    });

    this.summary?.addEventListener('click', this.#handleCheckout, true);
    this.#updateVisibility(false);
  }

  disconnectedCallback() {
    clearTimeout(this.#saveTimer);
    clearTimeout(this.#visibilityTimer);

    this.toggle?.removeEventListener('change', this.#handleToggle);
    this.fields?.forEach((field) => {
      field.removeEventListener('input', this.#handleInput);
      field.removeEventListener('change', this.#handleInput);
    });

    this.summary?.removeEventListener('click', this.#handleCheckout, true);
  }

  #handleToggle = () => {
    this.#dirty = true;
    this.#updateVisibility();
    this.#queueSave();
  };

  #handleInput = () => {
    this.#dirty = true;
    this.#setStatus('');
    this.#queueSave();
  };

  #updateVisibility(animate = true) {
    const enabled = this.toggle.checked;

    this.fields.forEach((field) => {
      field.required = enabled && field.dataset.required === 'true';
    });

    clearTimeout(this.#visibilityTimer);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const clearAnimationStyles = () => {
      this.panel.style.removeProperty('max-height');
      this.panel.style.removeProperty('opacity');
      this.panel.style.removeProperty('transform');
      this.panel.style.removeProperty('overflow');
      this.panel.style.removeProperty('transition');
    };

    if (!animate || reduceMotion) {
      clearAnimationStyles();
      this.panel.hidden = !enabled;
      return;
    }

    if (enabled) {
      this.panel.hidden = false;
      this.panel.style.transition = 'none';
      this.panel.style.maxHeight = '0px';
      this.panel.style.opacity = '0';
      this.panel.style.transform = 'translateY(-6px)';
      this.panel.style.overflow = 'hidden';

      void this.panel.offsetHeight;

      this.panel.style.transition = [
        'max-height 320ms cubic-bezier(0.22, 1, 0.36, 1)',
        'opacity 220ms ease-out',
        'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)',
      ].join(', ');
      this.panel.style.maxHeight = `${this.panel.scrollHeight}px`;
      this.panel.style.opacity = '1';
      this.panel.style.transform = 'translateY(0)';

      this.#visibilityTimer = setTimeout(() => {
        if (this.toggle.checked) clearAnimationStyles();
      }, 350);
    } else {
      const currentHeight = this.panel.getBoundingClientRect().height || this.panel.scrollHeight;

      this.panel.hidden = false;
      this.panel.style.transition = 'none';
      this.panel.style.maxHeight = `${currentHeight}px`;
      this.panel.style.opacity = '1';
      this.panel.style.transform = 'translateY(0)';
      this.panel.style.overflow = 'hidden';

      void this.panel.offsetHeight;

      this.panel.style.transition = [
        'max-height 240ms ease-in-out',
        'opacity 180ms ease-in',
        'transform 220ms ease-in-out',
      ].join(', ');
      this.panel.style.maxHeight = '0px';
      this.panel.style.opacity = '0';
      this.panel.style.transform = 'translateY(-6px)';

      this.#visibilityTimer = setTimeout(() => {
        if (!this.toggle.checked) this.panel.hidden = true;
        clearAnimationStyles();
      }, 270);
    }
  }

  #queueSave() {
    clearTimeout(this.#saveTimer);
    this.#saveTimer = setTimeout(() => this.#save(), 700);
  }

  #getAttributes() {
    const enabled = this.toggle.checked;

    const attributes = {
      'Solicita factura': enabled ? 'SÃ­' : '',
    };

    this.fields.forEach((field) => {
      attributes[field.dataset.attribute] = enabled ? field.value.trim() : '';
    });

    return attributes;
  }

  async #save() {
    clearTimeout(this.#saveTimer);

    if (this.#saving || !this.#dirty) return;

    this.#saving = true;
    this.#setStatus('');

    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: this.#getAttributes(),
        }),
      });

      if (!response.ok) {
        throw new Error('No fue posible guardar los datos.');
      }

      this.#dirty = false;
      this.#setStatus('');
    } catch (error) {
      console.error('[invoice-fields]', error);
      this.#setStatus(
        'No pudimos guardar los datos. IntÃ©ntalo nuevamente.',
        true
      );

      throw error;
    } finally {
      this.#saving = false;
    }
  }

  #validate() {
    if (!this.toggle.checked) return true;

    const invalidField = this.fields.find((field) => !field.checkValidity());

    if (invalidField) {
      invalidField.reportValidity();
      invalidField.focus();
      return false;
    }

    return true;
  }

  #handleCheckout = async (event) => {
    if (!(event.target instanceof Element)) return;

    const checkoutButton = event.target.closest(
      'button[name="checkout"], a[href*="/checkout"], [data-checkout]'
    );

    if (!checkoutButton) return;

    if (!this.#validate()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!this.#dirty) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    checkoutButton.setAttribute('aria-disabled', 'true');

    try {
      await this.#save();

      const root = window.Shopify?.routes?.root || '/';
      window.location.assign(`${root}checkout`);
    } catch {
      checkoutButton.removeAttribute('aria-disabled');
    }
  };

  #setStatus(message, isError = false) {
    if (!this.status) return;

    this.status.textContent = message;
    this.status.dataset.error = isError ? 'true' : 'false';
  }
}

if (!customElements.get('invoice-fields')) {
  customElements.define('invoice-fields', InvoiceFieldsComponent);
}